/**
 * WriteApprovalMessage — agent-initiated ``doc_write_section``
 * approval rendered through the shared ``ProposalCard`` (the SAME
 * surface writer-initiated highlight edits use), so every text-edit
 * decision in the chat looks and works identically.
 *
 * The underlying mechanics are untouched: this is still the blocking
 * WebSocket write-approval gate (useUserInputBridge). Approve answers
 * the bridge with ``{approved: true, content}`` — where ``content``
 * is the (optionally writer-edited) after-buffer — and Reject answers
 * ``{approved: false}``, exactly the payloads the retired
 * UserInputInlineRow write_approval branch sent. Without a connected
 * bridge the buttons disable (the server fail-closes on timeout).
 *
 * Renders null when the request isn't a parseable write approval —
 * the caller falls back to UserInputInlineRow, which still owns the
 * non-text-edit input types (api_failure, literature approval,
 * generic forms).
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { parseWriteApprovalPayload } from "../../components/WriteApprovalView";
import type { UserInputRequest } from "../../services/documentBridge";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import ProposalCard from "./ProposalCard";

export interface WriteApprovalMessageProps {
  request: UserInputRequest;
  /** True when the WS bridge is connected — buttons disable otherwise
   *  (an answer over a dead socket goes nowhere). */
  bridgeConnected: boolean;
  /** Answer the bridge (same contract as UserInputInlineRow.onApprove:
   *  the serialized decision payload). */
  onSubmit: (value: string) => void;
}

const useStyles = makeStyles({
  editToggle: {
    marginTop: "6px",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    textDecoration: "underline",
  },
  textarea: {
    width: "100%",
    marginTop: "4px",
    minHeight: "100px",
    padding: "8px 10px",
    fontFamily: fonts.mono,
    fontSize: "11.5px",
    lineHeight: 1.5,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "6px",
    background: "#fff",
    color: inkColors.ink,
    resize: "vertical",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 0 0 2px ${brandColors.glow}`,
    },
  },
});

export const WriteApprovalMessage: React.FC<WriteApprovalMessageProps> = ({ request, bridgeConnected, onSubmit }) => {
  const styles = useStyles();
  const payload = useMemo(() => parseWriteApprovalPayload(request.question), [request.question]);

  // Editable after-buffer — the writer can adjust the proposed content
  // before approving; the edited text is what the agent writes.
  const [editedAfter, setEditedAfter] = useState<string>("");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (payload) {
      setEditedAfter(payload.after);
      setEditing(false);
    }
  }, [payload]);

  if (!payload) return null;

  const sectionLabel = `§${payload.section_number}`;
  const header = payload.reason
    ? `Proposed ${payload.mode === "overwrite" ? "overwrite of" : "insert into"} ${sectionLabel} — ${payload.reason}`
    : `Proposed ${payload.mode === "overwrite" ? "overwrite of" : "insert into"} ${sectionLabel}`;

  const extraBody = (
    <>
      <button type="button" className={styles.editToggle} onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
        {editing ? "done editing" : "edit before approving"}
      </button>
      {editing && (
        <textarea
          className={styles.textarea}
          value={editedAfter}
          onChange={(e) => setEditedAfter(e.target.value)}
          spellCheck={false}
          aria-label="Edit the proposed content before approving"
        />
      )}
    </>
  );

  return (
    <ProposalCard
      chipText="Write approval"
      header={header}
      // Preview the (possibly writer-edited) content that Approve will
      // actually send — keeps the preview honest while editing.
      previewMarkdown={editedAfter}
      originalText={payload.before || null}
      warning={bridgeConnected ? null : "Connection to the agent was lost — this request can no longer be answered."}
      extraBody={extraBody}
      metaChips={[sectionLabel, payload.mode === "overwrite" ? "Overwrite" : "Insert"]}
      disabled={!bridgeConnected}
      ariaLabel={`Write approval for ${sectionLabel}`}
      onApprove={() => onSubmit(JSON.stringify({ approved: true, content: editedAfter }))}
      onReject={() => onSubmit(JSON.stringify({ approved: false }))}
    />
  );
};

export default WriteApprovalMessage;
